import { NavigationTabs } from "@/components/ui/navigation-tabs";
import { PagePagination } from "@/components/ui/page-pagination";
import { PageWithTitle } from "@common/components/PageLayout/PageWithTitle";
import { DataTable } from "@common/components/Table/DataTable";
import { useState } from "react";
import {
  AdminStipendsSelectedTab,
  useAdminStipendsTabs,
} from "./useAdminStipendsTabs";
import { useStipendsAdminColumns } from "./useStipendsAdminColumns";
import { useStipendsData } from "./useStipendsData";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { Card } from "@/components/ui/card";
import { ClockClockwise, FileDashed } from "@phosphor-icons/react";
import { NoResultsCard } from "@common/components/NoResultsCard";
import { StipendManager } from "./components/StipendManager/StipendManager";

interface Props {
  tab: AdminStipendsSelectedTab;
}

const noResultsContent = {
  [AdminStipendsSelectedTab.History]: {
    Icon: FileDashed,
    header: "No approved or denied stipends",
    body: "No stipends have been approved or denied yet",
  },
  [AdminStipendsSelectedTab.Pending]: {
    Icon: ClockClockwise,
    header: "No pending stipends",
    body: "You have no pending stipends.",
  },
};

export const StipendsAdmin = ({ tab }: Props) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { status, tabs } = useAdminStipendsTabs(tab, setCurrentPage);

  const { columns } = useStipendsAdminColumns(tab);
  const { loading, stipends, hasMorePages } = useStipendsData(
    currentPage,
    status,
    setCurrentPage,
  );

  const data = (
    <>
      <Card className="mt-5">
        <DataTable columns={columns} data={stipends} />
      </Card>
      <PagePagination
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        onChange={setCurrentPage}
      />
    </>
  );

  if (tab === AdminStipendsSelectedTab.StipendManager) {
    return (
      <PageWithTitle title="Stipend Manager">
        <NavigationTabs tabs={tabs} selectedTab={tab} />
        <StipendManager />
      </PageWithTitle>
    );
  }

  const noResults = <NoResultsCard {...noResultsContent[tab]} />;

  return (
    <PageWithTitle title="Stipends">
      <NavigationTabs tabs={tabs} selectedTab={tab} />
      {loading && stipends.length === 0 ? (
        <FullPageLoader />
      ) : stipends.length === 0 ? (
        noResults
      ) : (
        data
      )}
    </PageWithTitle>
  );
};
